import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  MonetizationOn,
  Category,
  Agriculture,
  Visibility,
  Analytics,
  Assessment
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

interface MarketAnalytics {
  summary: {
    total_requirements: number;
    active_requirements: number;
    fulfilled_requirements: number;
    expired_requirements: number;
    total_market_value: number;
    avg_requirement_value: number;
  };
  categories: Array<{
    category: string;
    count: number;
    total_value: number;
  }>;
  top_products: Array<{
    product_name: string;
    product_type: string;
    demand_count: number;
    total_quantity: number;
    unit: string;
    avg_max_price: number;
  }>;
}

interface PurchaseRequirement {
  id: string;
  title: string;
  category: string;
  total_budget: number;
  currency: string;
  delivery_location: any;
  delivery_date_from: string;
  delivery_date_to: string;
  buyer_first_name: string;
  buyer_last_name: string;
  items: Array<{
    product_name: string;
    quantity: number;
    unit: string;
    unit_price_max?: number;
  }>;
  interested_farmers: number;
}

const MarketAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<MarketAnalytics | null>(null);
  const [requirements, setRequirements] = useState<PurchaseRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequirement, setSelectedRequirement] = useState<PurchaseRequirement | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchActiveRequirements();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/purchase-requirements/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch analytics');
      }

      setAnalytics(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics');
    }
  };

  const fetchActiveRequirements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/purchase-requirements/admin/all?status=active&limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch requirements');
      }

      setRequirements(data.data.requirements);
    } catch (err: any) {
      console.error('Error fetching requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' } = {
      crops: 'success',
      fruits: 'warning',
      livestock: 'info',
      processed: 'secondary'
    };
    return colors[category] || 'primary';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No analytics data available
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Analytics color="primary" />
          Market Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor market demand, track purchase requirements, and analyze agricultural marketplace trends
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Requirements
                  </Typography>
                  <Typography variant="h4">
                    {analytics.summary.total_requirements}
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Requirements
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {analytics.summary.active_requirements}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Market Value
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.summary.total_market_value)}
                  </Typography>
                </Box>
                <MonetizationOn sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Requirement Value
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.summary.avg_requirement_value)}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Category Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Category />
              Category Breakdown
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.categories.map((category) => (
                    <TableRow key={category.category}>
                      <TableCell>
                        <Chip
                          label={category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                          color={getCategoryColor(category.category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{category.count}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(category.total_value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Products in Demand */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Agriculture />
              Top Products in Demand
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Demand</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Avg Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.top_products.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {product.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.product_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{product.demand_count}</TableCell>
                      <TableCell align="right">
                        {product.total_quantity.toLocaleString()} {product.unit}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(product.avg_max_price)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Active Requirements */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart />
              Active Purchase Requirements
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Buyer</TableCell>
                    <TableCell align="right">Budget</TableCell>
                    <TableCell>Delivery Date</TableCell>
                    <TableCell align="center">Interested Farmers</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requirements && requirements.length > 0 ? (
                    requirements.slice(0, 10).map((requirement) => (
                      <TableRow key={requirement.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {requirement.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={requirement.category}
                            color={getCategoryColor(requirement.category)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {requirement.buyer_first_name} {requirement.buyer_last_name}
                        </TableCell>
                        <TableCell align="right">
                          {requirement.total_budget ? formatCurrency(requirement.total_budget) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {formatDate(requirement.delivery_date_from)} - {formatDate(requirement.delivery_date_to)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={requirement.interested_farmers || 0}
                            color={requirement.interested_farmers > 0 ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => {
                              setSelectedRequirement(requirement);
                              setDetailsDialog(true);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary" py={3}>
                          No active requirements found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Requirement Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedRequirement && (
          <>
            <DialogTitle>
              Purchase Requirement Details
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {selectedRequirement.title}
                  </Typography>
                  <Chip
                    label={selectedRequirement.category}
                    color={getCategoryColor(selectedRequirement.category)}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Buyer Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedRequirement.buyer_first_name} {selectedRequirement.buyer_last_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Budget:</strong> {selectedRequirement.total_budget ? formatCurrency(selectedRequirement.total_budget) : 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Delivery Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> {selectedRequirement.delivery_location?.region}, {selectedRequirement.delivery_location?.district}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date Range:</strong> {formatDate(selectedRequirement.delivery_date_from)} - {formatDate(selectedRequirement.delivery_date_to)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Product Requirements
                  </Typography>
                  <List dense>
                    {selectedRequirement.items.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.product_name}
                          secondary={`${item.quantity} ${item.unit}${item.unit_price_max ? ` - Max price: ${formatCurrency(item.unit_price_max)}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Interested Farmers:</strong> {selectedRequirement.interested_farmers || 0}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MarketAnalytics;
